import { useState } from 'react';
import { FolderTree, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@ui/button.tsx';
import { Card, CardContent } from '@ui/card.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';
import { useAdminShopCategoriesCollection } from './custom-shop-categories-api.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { isAdminOrHigher, type Role } from '@r/utils/roles.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { CreateCustomShopCategoryForm } from './create-custom-shop-category-form.tsx';
import { CustomShopCategoriesFlow } from './custom-shop-categories-flow.tsx';

interface CustomShopCategoriesCatalogProps {
  shopId: number;
}

/** Section catégories d'une boutique custom avec éditeur React Flow. */
export function CustomShopCategoriesCatalog({ shopId }: CustomShopCategoriesCatalogProps) {
  const { t } = useTranslation();
  const user = useUser();
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const canManageCategories = isAdminOrHigher(activeWorkspace?.maxRole as Role);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const categoriesQuery = useAdminShopCategoriesCollection(shopId);
  useOrvalQueryErrorToast(categoriesQuery, t('admin.shops.categories.error.load'));

  const categories = categoriesQuery.data ?? [];

  return (
    <>
      <Card className="w-full" data-shop-id={shopId}>
        <CardContent className="p-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-2">
              <h2 className="flex gap-3 items-center text-2xl font-bold">
                <FolderTree className="size-6" />
                {t('admin.shops.categories.title')}
              </h2>
              <p className="text-muted-foreground">{t('admin.shops.categories.description')}</p>
            </div>

            {canManageCategories && (
              <Button type="button" rounded="full" onClick={() => setIsCreateDialogOpen(true)}>
                {t('admin.shops.categories.create.button')}
                <Plus className="size-4" />
              </Button>
            )}
          </div>

          <div className="mt-6">
            {categoriesQuery.isLoading && <Skeleton className="h-[28rem] w-full rounded-xl" />}

            {categoriesQuery.isError && (
              <div className="flex flex-col gap-4 items-center py-10 text-center rounded-xl border">
                <p className="text-muted-foreground">{t('admin.shops.categories.error.load')}</p>
                <Button
                  type="button"
                  variant="outline"
                  rounded="full"
                  onClick={() => void categoriesQuery.refetch()}
                >
                  {t('admin.shops.categories.error.retry')}
                </Button>
              </div>
            )}

            {categoriesQuery.isSuccess && categories.length === 0 && (
              <p className="px-6 py-10 text-center rounded-xl border border-dashed text-muted-foreground">
                {t('admin.shops.categories.empty')}
              </p>
            )}

            {categoriesQuery.isSuccess && categories.length > 0 && (
              <CustomShopCategoriesFlow
                shopId={shopId}
                categories={categories}
                canManageCategories={canManageCategories}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {canManageCategories && (
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="overflow-hidden p-0 max-w-xl">
            <div className="scrollable max-h-[90vh] overflow-y-auto p-8 md:p-14">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {t('admin.shops.categories.create.dialog.title')}
                </DialogTitle>
                <DialogDescription>
                  {t('admin.shops.categories.create.dialog.description')}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-6">
                <CreateCustomShopCategoryForm
                  shopId={shopId}
                  onCancel={() => setIsCreateDialogOpen(false)}
                  onSuccess={() => setIsCreateDialogOpen(false)}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
